import type { ChartData, ChartOptions, TooltipItem } from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { useMemo } from 'react';

import type { JobSubmissionBucket } from '../types/dashboardTypes';
import { formatHourLabel, formatSeconds } from '../utils';

type TProps = {
  submissionBuckets: JobSubmissionBucket[];
};

const readCssVar = (varName: `--${string}`, fallback: string) => {
  if (typeof window === 'undefined') {
    return fallback;
  }
  const value = window.getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  return value.length > 0 ? value : fallback;
};

export const JobSubmissionsCard = ({ submissionBuckets }: TProps) => {
  const totalSubmittedJobs = submissionBuckets.reduce((sum, bucket) => sum + (bucket.jobCount ?? 0), 0);
  const totalSubmissionDuration = submissionBuckets.reduce(
    (sum, bucket) => sum + (bucket.totalDurationSeconds ?? 0),
    0,
  );

  const chartColors = useMemo(
    () => ({
      tint: readCssVar('--color-tint', '#3b82f6'),
      accent: readCssVar('--color-accent', '#2563eb'),
      accentSubtle: readCssVar('--color-accent-subtle', 'rgba(37, 99, 235, 0.2)'),
      divider: readCssVar('--color-divider', 'rgba(0,0,0,0.1)'),
    }),
    [],
  );

  const submissionChartData: ChartData<'bar' | 'line', number[], string> = {
    labels: submissionBuckets.map((bucket) => formatHourLabel(bucket.hour)),
    datasets: [
      {
        type: 'bar',
        label: 'Jobs submitted',
        data: submissionBuckets.map((bucket) => bucket.jobCount),
        backgroundColor: chartColors.tint,
        borderRadius: 4,
        yAxisID: 'y',
        order: 0,
      },
      {
        type: 'bar',
        label: 'Total duration (min)',
        data: submissionBuckets.map((bucket) => (bucket.totalDurationSeconds ?? 0) / 60),
        borderColor: chartColors.accent,
        backgroundColor: chartColors.accentSubtle,
        borderRadius: 4,
        yAxisID: 'y1',
        order: 1,
      },
    ],
  };

  const submissionChartOptions: ChartOptions<'bar' | 'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const,
      },
      tooltip: {
        callbacks: {
          label: (context: TooltipItem<'bar' | 'line'>) => `${context.dataset.label}: ${context.formattedValue}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        grid: { color: chartColors.divider },
        title: { display: true, text: 'Jobs' },
      },
      y1: {
        beginAtZero: true,
        position: 'right' as const,
        grid: { drawOnChartArea: false },
        title: { display: true, text: 'Duration (minutes)' },
      },
    },
  };

  return (
    <div className="bg-[var(--color-surface)] shadow-[0_8px_10px_rgba(0,0,0,0.05)] rounded-[10px] p-5 xl:col-span-3">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[var(--color-text-muted)] font-medium text-lg leading-[150%]">
          Job submissions (last 24h)
        </div>
        <div className="text-xs text-[var(--color-text-muted)]">
          Total jobs: {totalSubmittedJobs} • Total duration: {formatSeconds(totalSubmissionDuration)}
        </div>
      </div>
      {submissionBuckets.length === 0 ? (
        <div className="p-2.5">No submissions in the last 24 hours.</div>
      ) : (
        <div className="h-[260px]">
          <Chart type="bar" options={submissionChartOptions} data={submissionChartData} />
        </div>
      )}
    </div>
  );
};
